import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase, BlogCategory } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { Save, ArrowLeft, Globe, Search, Eye, X, Calendar, Clock, User, Star } from 'lucide-react';
import { RichEditor } from '@/components/admin/RichEditor';
import { ImageUploader } from '@/components/admin/ImageUploader';

export function AdminBlogEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [previewMode, setPreviewMode] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    cover_image: '',
    category_id: '',
    status: 'draft',
    is_featured: false,
    meta_title: '',
    meta_description: '',
    keywords: ''
  });

  useEffect(() => {
    loadCategories();
    if (id) loadPost();
  }, [id]);

  async function loadCategories() {
    const { data } = await supabase.from('blog_categories').select('*');
    if (data) setCategories(data);
  }

  async function loadPost() {
    const { data } = await supabase.from('blog_posts').select('*').eq('id', id).single();
    if (data) {
      setFormData({
        title: data.title,
        slug: data.slug,
        excerpt: data.excerpt || '',
        content: data.content || '',
        cover_image: data.image_url || '', 
        category_id: data.category_id || '',
        status: data.status,
        is_featured: data.is_featured || false,
        meta_title: data.meta_title || '',
        meta_description: data.meta_description || '',
        keywords: Array.isArray(data.keywords) ? data.keywords.join(', ') : (data.keywords || '')
      });
    }
  }

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      slug: !id ? title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') : prev.slug
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.content) return alert('Título y contenido son obligatorios');
    
    setLoading(true);
    
    const payload = {
      title: formData.title,
      slug: formData.slug,
      excerpt: formData.excerpt,
      content: formData.content,
      image_url: formData.cover_image,
      category_id: formData.category_id || null,
      status: formData.status,
      is_featured: formData.is_featured,
      meta_title: formData.meta_title,
      meta_description: formData.meta_description,
      keywords: formData.keywords.split(',').map(k => k.trim()).filter(k => k),
      author_id: user?.id,
      published_at: formData.status === 'published' ? new Date().toISOString() : null
    };

    let error;
    if (id) {
      const { error: err } = await supabase.from('blog_posts').update(payload).eq('id', id);
      error = err;
    } else {
      const { error: err } = await supabase.from('blog_posts').insert(payload);
      error = err;
    }

    setLoading(false);
    if (!error) navigate('/admin/blog');
    else alert('Error al guardar: ' + error.message);
  };

  // --- VISTA PREVIA ---
  if (previewMode) {
    const categoryName = categories.find(c => c.id === formData.category_id)?.name || 'Categoría';
    const readTime = Math.ceil((formData.content?.split(/\s+/).length || 0) / 200);

    return (
        <div className="bg-white min-h-screen pb-20 relative z-50">
            {/* Barra de control Preview */}
            <div className="fixed top-0 left-0 right-0 bg-slate-900 text-white p-4 flex justify-between items-center z-50 shadow-lg">
                <div className="flex items-center gap-2">
                    <Eye className="w-5 h-5 text-green-400" />
                    <span className="font-bold">Modo Vista Previa</span>
                </div>
                <button 
                    onClick={() => setPreviewMode(false)}
                    className="bg-white text-slate-900 px-4 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-slate-100 transition-colors"
                >
                    <X className="w-4 h-4" /> Cerrar Vista Previa
                </button>
            </div>

            {/* Simulación del Header */}
            {formData.cover_image && (
                <div className="w-full h-[40vh] md:h-[50vh] relative mt-16">
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent z-10"></div>
                    <img src={formData.cover_image} alt={formData.title} className="w-full h-full object-cover" />
                    <div className="absolute bottom-0 left-0 w-full z-20 p-4 md:p-8">
                        <div className="max-w-7xl mx-auto px-4">
                            <span className="inline-block bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide mb-4">
                                {categoryName}
                            </span>
                            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
                                {formData.title || 'Título del Artículo'}
                            </h1>
                            <div className="flex items-center gap-6 text-slate-200 text-sm">
                                <span className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4" />
                                    {new Date().toLocaleDateString()}
                                </span>
                                <span className="flex items-center gap-2">
                                    <Clock className="w-4 h-4" />
                                    {readTime} min lectura
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* CORRECCIÓN: Ampliado a max-w-7xl para simular la web real */}
            <div className={`max-w-7xl mx-auto px-4 py-8 md:py-12 ${!formData.cover_image ? 'mt-20' : ''}`}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <article className="lg:col-span-8 prose prose-lg prose-slate max-w-none text-justify hyphens-auto">
                        {!formData.cover_image && <h1 className="mb-8">{formData.title}</h1>}
                        <div dangerouslySetInnerHTML={{ __html: formData.content || '<p>Contenido del artículo...</p>' }} />
                    </article>
                    <aside className="lg:col-span-4 space-y-8">
                        <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
                            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                                <User className="w-5 h-5 text-blue-600" /> Escrito por
                            </h3>
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-xl">E</div>
                                <div>
                                    <p className="font-bold text-slate-900">Equipo Editorial</p>
                                    <p className="text-xs text-slate-500">EncuentraTrastero.com</p>
                                </div>
                            </div>
                        </div>
                        
                        {/* Simulación del Anuncio Lateral */}
                        <div className="bg-slate-900 text-white p-8 rounded-2xl shadow-xl sticky top-24">
                            <h3 className="text-xl font-bold mb-3">¿Tienes un trastero?</h3>
                            <p className="text-slate-300 mb-6 text-sm text-left">
                                Miles de personas buscan espacio cada día. Publica tu anuncio gratis.
                            </p>
                            <button className="block w-full text-center bg-blue-600 text-white font-bold py-3.5 rounded-xl">
                                Publicar Gratis
                            </button>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
  }

  // --- VISTA EDITOR ---
  return (
    <form onSubmit={handleSubmit} className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8 sticky top-0 bg-slate-50 py-4 z-10 border-b border-slate-200">
        <div className="flex items-center gap-4">
          <button type="button" onClick={() => navigate('/admin/blog')} className="p-2 hover:bg-slate-200 rounded-full text-slate-500">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-slate-900">{id ? 'Editar Artículo' : 'Nuevo Artículo'}</h1>
        </div>
        <div className="flex gap-3 items-center">
          
          <label className="flex items-center gap-2 cursor-pointer bg-white px-3 py-2 rounded-lg border border-slate-300 hover:bg-slate-50">
            <input 
                type="checkbox"
                checked={formData.is_featured}
                onChange={e => setFormData({...formData, is_featured: e.target.checked})}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-slate-700 flex items-center gap-1">
                <Star className={`w-4 h-4 ${formData.is_featured ? 'fill-yellow-400 text-yellow-400' : 'text-slate-400'}`} />
                Destacar en Home
            </span>
          </label>

          <button 
            type="button" 
            onClick={() => setPreviewMode(true)}
            className="bg-white text-slate-700 border border-slate-300 px-4 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-slate-50"
          >
            <Eye className="w-4 h-4" /> Vista Previa
          </button>
          <select 
            value={formData.status}
            onChange={e => setFormData({...formData, status: e.target.value})}
            className="bg-white border border-slate-300 rounded-lg px-4 py-2 text-sm font-medium"
          >
            <option value="draft">Borrador</option>
            <option value="published">Publicado</option>
            <option value="archived">Archivado</option>
          </select>
          <button 
            type="submit" 
            disabled={loading}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4" /> Guardar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">Título H1</label>
                <input 
                  type="text" 
                  required
                  value={formData.title}
                  onChange={handleTitleChange}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg text-xl font-bold placeholder:text-slate-300"
                  placeholder="Escribe un título atractivo..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">Slug URL</label>
                <div className="flex items-center gap-2 text-slate-500 bg-slate-50 px-3 py-2 rounded-lg border border-slate-200">
                  <Globe className="w-4 h-4" />
                  <span className="text-xs">encuentratrastero.com/blog/</span>
                  <input 
                    type="text" 
                    required
                    value={formData.slug}
                    onChange={e => setFormData({...formData, slug: e.target.value})}
                    className="flex-1 bg-transparent outline-none text-slate-900 font-medium"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Contenido</label>
                <RichEditor 
                    value={formData.content}
                    onChange={(val) => setFormData({...formData, content: val})}
                    placeholder="Escribe tu artículo aquí... Puedes arrastrar imágenes directamente."
                />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 border-l-4 border-l-purple-500">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Search className="w-5 h-5 text-purple-600" /> SEO Manager
            </h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Meta Title</label>
                  <span className={`text-xs ${formData.meta_title.length > 60 ? 'text-red-500' : 'text-green-600'}`}>
                    {formData.meta_title.length}/60
                  </span>
                </div>
                <input 
                  type="text" 
                  value={formData.meta_title}
                  onChange={e => setFormData({...formData, meta_title: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                  placeholder="Título optimizado para Google"
                />
              </div>
              <div>
                <div className="flex justify-between">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Meta Description</label>
                  <span className={`text-xs ${formData.meta_description.length > 160 ? 'text-red-500' : 'text-green-600'}`}>
                    {formData.meta_description.length}/160
                  </span>
                </div>
                <textarea 
                  rows={3}
                  value={formData.meta_description}
                  onChange={e => setFormData({...formData, meta_description: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg text-sm"
                  placeholder="Descripción que aparecerá en los resultados de búsqueda..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Keywords</label>
                <input 
                  type="text" 
                  value={formData.keywords}
                  onChange={e => setFormData({...formData, keywords: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                  placeholder="trasteros, alquiler, precios... (separadas por coma)"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <h3 className="font-bold text-slate-900 mb-4">Configuración</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Categoría</label>
                <select 
                  value={formData.category_id}
                  onChange={e => setFormData({...formData, category_id: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                >
                  <option value="">Seleccionar...</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Imagen Destacada</label>
                <ImageUploader 
                    value={formData.cover_image}
                    onChange={(url) => setFormData({...formData, cover_image: url})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Extracto (Resumen)</label>
                <textarea 
                  rows={4}
                  value={formData.excerpt}
                  onChange={e => setFormData({...formData, excerpt: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg text-sm"
                  placeholder="Breve resumen para el listado..."
                />
              </div>
            </div>
          </div>
        </div>

      </div>
    </form>
  );
}
